import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

// Components
import FilterBar from "../../../components/Filters/FilterBar";
import HeaderBar from "../../../components/Header/HeaderBar";
import LabeledTextboxInput from "../../../components/Inputs/LabeledTextboxInput";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import MultipleChoiceModal from "../../../components/Modals/MultipleChoiceModal";
import SubtleButton from "../../../components/Buttons/SubtleButton";

// Redux
import { updateUser } from "../../../store/reducers/currentUserSlice";

// Services
import DeactivateConfirmModal from "../../../components/Modals/DeactivateConfirmModal";
import axiosCompanies from "../../../services/axios/companies";
import axiosUsers from "../../../services/axios/users";
import flash from "../../../services/flash";
import { resizeImageToPng } from "../../../utils/resizeImage";
import { useQueryClient } from "@tanstack/react-query";
import Until from "../../../components/Until";
import CompanyLocations from "./UpdateCompanyPage/CompanyLocations";
import { useFeatureFlag, User } from "configcat-react";
import CopyableText from "../../../components/CopyableText";

const AdminUpdateCompanyPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentFilter, setCurrentFilter] = useState("");

  let params = useParams();
  let companyId = props.companyId ?? params.companyId;

  const [company, setCompany] = useState({});
  const [displayConfirmAddLicenseModal, setDisplayConfirmAddLicenseModal] =
    useState(false);
  const [
    displayConfirmRemoveLicenseModal,
    setDisplayConfirmRemoveLicenseModal,
  ] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingLogos, setPendingLogos] = useState({});
  const [pendingPreviews, setPendingPreviews] = useState({});
  const [removedLogos, setRemovedLogos] = useState([]);
  const pendingPreviewsRef = useRef(pendingPreviews);
  pendingPreviewsRef.current = pendingPreviews;

  useEffect(() => {
    return () => {
      Object.values(pendingPreviewsRef.current).forEach((url) =>
        URL.revokeObjectURL(url),
      );
    };
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    axiosCompanies.getCompanyById({ companyId }, (data) => {
      setCompany(data);
      setLoading(false);
    });
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [companyId, fetchData]);

  useEffect(() => {
    if (company?.temperature === "C") {
      setCurrentFilter("C");
    } else setCurrentFilter("F");
  }, [company]);

  const handleSaveCompany = useCallback(async () => {
    setLoading(true);
    company.temperature = currentFilter;

    let uploadedLogos = {};
    try {
      for (const [season, blob] of Object.entries(pendingLogos)) {
        const { url, logoUrl } = await axiosCompanies.getLogoUploadUrl(
          companyId,
          season,
        );
        const putRes = await fetch(url, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": "image/png" },
        });
        if (!putRes.ok) throw new Error("S3 upload failed");
        uploadedLogos[season] = `${logoUrl}?t=${Date.now()}`;
      }
    } catch (err) {
      flash.error("Failed to upload logo. Please try again.");
      setLoading(false);
      return;
    }

    const mergedEmailLogos = { ...company.emailLogos, ...uploadedLogos };
    const payloadEmailLogos = { ...mergedEmailLogos };
    removedLogos.forEach((season) => {
      payloadEmailLogos[season] = null;
    });

    axiosCompanies.updateCompanyById(
      {
        ...company,
        companyId: company._id,
        emailLogos: payloadEmailLogos,
        emailReportReplyTo: company.emailReportReplyTo.filter(
          (a) => a.trim() !== "",
        ),
      },
      (data) => {
        flash.success("Company settings updated!");
        Object.values(pendingPreviews).forEach((url) =>
          URL.revokeObjectURL(url),
        );
        setPendingLogos({});
        setPendingPreviews({});
        setRemovedLogos([]);
        setCompany({
          ...company,
          emailLogos: payloadEmailLogos,
          locations: data.company.locations,
        });
        axiosUsers.getUserById({ uid: props.currentUser.uid }, (userData) => {
          dispatch(updateUser(userData));
          setLoading(false);
        });
      },
      () => {
        flash.error("Had an error trying to save company settings");
        setLoading(false);
      },
    );
  }, [
    company,
    companyId,
    currentFilter,
    dispatch,
    pendingLogos,
    pendingPreviews,
    removedLogos,
    props.currentUser.uid,
  ]);

  const handleAddCompanyLicense = useCallback(() => {
    if (!company) return;
    axiosCompanies.addCompanyLicense(
      { companyId: company._id, uid: props.currentUser.uid },
      () => {
        setDisplayConfirmAddLicenseModal(false);
        flash.success("Successfully Activated " + company.name);
        // Refresh data
        fetchData();
        queryClient.invalidateQueries({
          queryKey: [`user/${props.currentUser.uid}/companies`],
        });
        queryClient.invalidateQueries({
          queryKey: [`user/${props.currentUser.uid}/account`],
        });
      },
      (err) => {
        setDisplayConfirmAddLicenseModal(false);
        flash.error(
          err.data?.message ??
            "Unable to activate this company right now. Please contact support",
        );
        // If error caused by user unsubscribed or undersubscribed, display upgrade modal
        if (err.data.needsUpgrade) {
          alert(
            "Please upgrade your subscription before activating this company",
          );
          navigate("/admin/settings/billing");
        }
      },
    );
  }, [company, fetchData, navigate, queryClient, props.currentUser.uid]);

  const handleRemoveCompanyLicense = useCallback(async () => {
    if (!company) return;

    axiosCompanies.removeCompanyLicense(
      { companyId: company._id, uid: props.currentUser.uid },
      () => {
        setDisplayConfirmRemoveLicenseModal(false);
        flash.success("Successfully Deactivated " + company.name);
        // Refresh data
        fetchData();
        queryClient.invalidateQueries({
          queryKey: [`user/${props.currentUser.uid}/companies`],
        });
        queryClient.invalidateQueries({
          queryKey: [`user/${props.currentUser.uid}/account`],
        });
      },
      (err) => {
        setDisplayConfirmRemoveLicenseModal(false);
        alert(err.message ?? err.data.message);
        console.error("Error in handleLicenseCompany: ", err);
        alert(err);
      },
    );
  }, [company, fetchData, queryClient, props.currentUser.uid]);

  const handleLogoPick = async (season, file) => {
    if (!file.type.startsWith("image/")) {
      flash.error("Please select an image file");
      return;
    }
    try {
      const blob = await resizeImageToPng(file, 1600, 1600); // max dimensions; only scales down
      const previewUrl = URL.createObjectURL(blob);
      if (pendingPreviews[season]) URL.revokeObjectURL(pendingPreviews[season]);
      setPendingLogos({ ...pendingLogos, [season]: blob });
      setPendingPreviews({ ...pendingPreviews, [season]: previewUrl });
      setRemovedLogos(removedLogos.filter((s) => s !== season));
      flash.success("Logo ready. Click Save to apply.");
    } catch (err) {
      flash.error("Could not process the image. Please try a different file.");
    }
  };

  const handleLogoRemove = (season) => {
    if (pendingPreviews[season]) {
      URL.revokeObjectURL(pendingPreviews[season]);
      const nextPending = { ...pendingLogos };
      const nextPreviews = { ...pendingPreviews };
      delete nextPending[season];
      delete nextPreviews[season];
      setPendingLogos(nextPending);
      setPendingPreviews(nextPreviews);
    }
    if (company.emailLogos?.[season]) {
      setRemovedLogos([...removedLogos.filter((s) => s !== season), season]);
      const updated = { ...company.emailLogos };
      delete updated[season];
      setCompany({ ...company, emailLogos: updated });
    }
    flash.success("Logo removed. Click Save to apply.");
  };

  let buttons = [];
  if (company.isPaid) {
    buttons.push({
      // color: "red",
      label: "Deactivate",
      onClick: () => setDisplayConfirmRemoveLicenseModal(true),
    });
  } else {
    buttons.push({
      color: "green",
      label: "Activate",
      onClick: () => setDisplayConfirmAddLicenseModal(true),
    });
  }
  buttons.push({
    color: "green",
    label: "Save",
    onClick: handleSaveCompany,
  });

  return (
    <>
      <div className="flex flex-col items-center">
        <HeaderBar buttons={buttons} title="Company Settings" />
        {loading ? (
          <LoadingIcon />
        ) : (
          <div className="flex flex-1 flex-col w-full p-10 items-center overflow-y-scroll">
            {company.isPaid ? null : (
              <div className="w-full text-left bg-snow-red text-full-white p-4 rounded-md mb-2">
                Note: This company is inactive. Employees will not be able to
                use this company in the field.
              </div>
            )}
            <LabeledTextboxInput
              label="Company Name"
              maxLength={50}
              placeholder="Name your company..."
              setValue={(val) => {
                setCompany({ ...company, name: val });
              }}
              value={company.name}
            />
            <LabeledTextboxInput
              disabled={true}
              label="My Role"
              value={props.currentUser.companies[company._id].role}
            />
            <LabeledTextboxInput
              disabled={true}
              label="Company Owner"
              value={company.owner.firstName + " " + company.owner.lastName}
            />
            <LabeledTextboxInput
              label="Company Name for Email Reports"
              description="This name will appear in the sender and subject line of email reports sent to clients."
              value={company.emailReportName ?? company.name}
              setValue={(val) => {
                setCompany({ ...company, emailReportName: val });
              }}
            />
            <LabeledTextboxInput
              label="Reply To Address for Email Reports"
              description="If a client replies to a report email, this is the email address that will get the response. Separate multiple values with a comma"
              value={company.emailReportReplyTo?.join(",") ?? ""}
              setValue={(val) => {
                val = val.trim();
                setCompany({
                  ...company,
                  emailReportReplyTo:
                    val.length > 0 ? val.split(",").map((s) => s.trim()) : [],
                });
              }}
              testId={"reply-to-email-reports"}
              placeholder="admin@example.com,demo@example.com"
            />
            {["snow", "landscape"].map((season) => {
              const previewSrc =
                pendingPreviews[season] || company.emailLogos?.[season];
              return (
                <div key={season} className="w-full mb-4">
                  <label className="text-sm font-bold capitalize">
                    {season} Email Report Logo
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    This logo will appear at the top of {season} email reports.
                  </p>
                  {previewSrc ? (
                    <div className="flex items-center gap-4">
                      <img
                        src={previewSrc}
                        alt={`${season} logo`}
                        className="h-16 object-contain"
                      />
                      <SubtleButton
                        label="Remove"
                        onClick={() => handleLogoRemove(season)}
                      />
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        id={`logo-upload-${season}`}
                        className="hidden"
                        data-testid={`logo-input-${season}`}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleLogoPick(season, file);
                        }}
                      />
                      <SubtleButton
                        onClick={() =>
                          document
                            .getElementById(`logo-upload-${season}`)
                            .click()
                        }
                      >
                        + Upload Logo
                      </SubtleButton>
                    </div>
                  )}
                </div>
              );
            })}
            <LabeledTextboxInput
              label="Hours Until Clock Out Reminder"
              description="Send a clock out reminder to foremen if their employees have been clocked in for longer than x hours.  Enter 0 to disable reminders."
              type="number"
              value={company.sendClockOutReminderAfterHours ?? 0}
              setValue={(val) => {
                setCompany({ ...company, sendClockOutReminderAfterHours: val });
              }}
            />

            <CompanyLocations
              companyId={company._id}
              initialLocations={company.locations ?? []}
            />

            <FilterBar
              currentFilter={currentFilter}
              filters={[
                { label: "Fahrenheit", value: "F" },
                { label: "Celsius", value: "C" },
              ]}
              setCurrentFilter={setCurrentFilter}
            />
            <p className="text-left w-full text-snow-primary">
              Company Id: <CopyableText text={company._id.toString()} />
            </p>
          </div>
        )}
      </div>
      {/* Add License To Company Modal */}
      <MultipleChoiceModal
        adjustForSidebar={true}
        open={displayConfirmAddLicenseModal}
        options={[
          {
            color: "green",
            label: "Activate",
            onClick: handleAddCompanyLicense,
          },
          {
            color: "blue",
            label: "Cancel",
            onClick: () => setDisplayConfirmAddLicenseModal(false),
          },
        ]}
        subtitle="Are you sure you want to activate this company?"
        title="Activate Company"
      />

      {/* Remove License To Company Modal */}
      <DeactivateConfirmModal
        adjustForSidebar={true}
        open={displayConfirmRemoveLicenseModal}
        options={[
          {
            color: "red",
            label: "Deactivate",
            onClick: handleRemoveCompanyLicense,
          },
          {
            color: "blue",
            label: "I've changed my mind",
            onClick: () => setDisplayConfirmRemoveLicenseModal(false),
          },
        ]}
        title="Deactivate Company"
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(AdminUpdateCompanyPage);
export { AdminUpdateCompanyPage };
